import { Document, Font, Image, PDFViewer, Page, Text, View } from '@react-pdf/renderer';

import RookeryBold from 'assets/fonts/Rookery-Bold.woff2';
import RookeryRegular from 'assets/fonts/Rookery-Regular.woff2';
import RookeryNewBold from 'assets/fonts/rookerynew/RookeryNew-Bold.otf';
import RookeryNewMedium from 'assets/fonts/rookerynew/RookeryNew-Medium.otf';
import RookeryNewRegular from 'assets/fonts/rookerynew/RookeryNew-Regular.otf';
import DimoLogo from 'assets/icons/DimoLogo.png';
import { RECEIPT } from 'common/constants/receipt';
import { useCallback, useEffect, useState } from 'react';
import { getLocalStorageData } from 'utils/saveLocalStorage';
import { IReceipt } from 'v3/pages/Receipt/types';
import * as api from 'v3/services/api';
import { FeatureFlagEnum } from 'v3/shared/enums/feature-flag.enum';
import { RookeryBoldFont, RookeryRegularFont } from 'v3/styles/common';
import { receiptFactory } from './factory';
import { styles } from './styles';

Font.register({ family: `${RookeryBoldFont}`, src: RookeryBold });
Font.register({ family: `${RookeryRegularFont}`, src: RookeryRegular });
Font.register({
  family: 'RookeryNew',
  fonts: [
    { src: RookeryNewBold, fontWeight: 'bold' },
    { src: RookeryNewMedium, fontWeight: 500 },
    { src: RookeryNewRegular }
  ]
});

const ReceiptConstructor = (receipt: IReceipt) => {
  const [sellerFeatureEnabled, setSellerFeatureEnabled] = useState<boolean>(false);
  const fetchSellerFeatureFlag = useCallback(async () => {
    const response = await api.getFlagStatus(FeatureFlagEnum.IKB2C_4429_ENABLE_SELLER_FEATURE);
    if (response.data.enabled) {
      setSellerFeatureEnabled(true);
    }
  }, []);

  useEffect(() => {
    fetchSellerFeatureFlag();
  }, []);
  return (
    <Document>
      <Page style={styles.body}>
        <View style={styles.header}>
          <Text> Comprovante de financiamento</Text>
          <Image src={DimoLogo} style={styles.image} />
        </View>
        <View style={styles.row}>
          <Text style={styles.introText}>Este financiamento foi efetuado com sucesso.</Text>
          <Text style={styles.subIntroText}>Feito em: {receipt.store.date}</Text>
        </View>
        <View style={styles.container}>
          <View style={styles.column}>
            <View style={styles.rowData}>
              <Text style={styles.title}>Dados do cliente</Text>
              <Text style={styles.title}>Detalhes</Text>
            </View>
            <View style={styles.dataContainer}>
              <View style={styles.rowClientData}>
                <View>
                  <Text style={styles.clientTitle}>Nome</Text>
                  <Text style={styles.clientSubtitle}>{receipt.person.fullName}</Text>
                </View>
                <View>
                  <Text style={styles.clientTitle}>Endereço</Text>
                  <Text style={styles.clientSubtitle}>{receipt.person.fullAddress}</Text>
                </View>
                <View>
                  <Text style={styles.clientTitle}>Consultor</Text>
                  <Text style={styles.clientSubtitle}>{receipt.seller.name}</Text>
                </View>
                <View>
                  <Text style={styles.clientTitle}>Modelo do aparelho</Text>
                  <Text style={styles.clientSubtitle}>{receipt.product.model}</Text>
                </View>
              </View>
              <View style={styles.rowClientData}>
                <View>
                  <Text style={styles.clientTitle}>CPF</Text>
                  <Text style={styles.clientSubtitle}>{receipt.person.cpf}</Text>
                </View>
                <View>
                  <Text style={styles.clientTitle}>Telefone</Text>
                  <Text style={styles.clientSubtitle}>{receipt.person.phoneNumber}</Text>
                </View>
                <View>
                  <Text style={styles.clientTitle}>Loja</Text>
                  <Text style={styles.clientSubtitle}>{receipt.store.name}</Text>
                </View>
                <View>
                  <Text style={styles.clientTitle}>IMEI</Text>
                  <Text style={styles.clientSubtitle}>{receipt.device.imei}</Text>
                </View>
              </View>
            </View>
          </View>
        </View>
        <View style={styles.container}>
          <Text style={styles.title}>Dados do financiamento</Text>
        </View>
        <View style={styles.dataContainer}>
          <View style={styles.column}>
            <View style={styles.rowFinancingData}>
              <Text style={styles.financingTitle}>Crédito aprovado</Text>
              <Text style={styles.financingSubtitle}>{receipt.product.releasedAmount}</Text>
            </View>
            <View style={styles.rowFinancingData}>
              <Text style={styles.financingTitle}>Valor do aparelho</Text>
              <Text style={styles.financingSubtitle}>{receipt.product.price}</Text>
            </View>
            <View style={styles.dashContainer}>
              {[...Array(35)].map((_, index) => (
                <View key={index} style={styles.dash} />
              ))}
            </View>
            <View style={styles.rowFinancingData}>
              <Text style={styles.clientTitle}>Itens adicionados</Text>
            </View>
            <View style={styles.rowFinancingData}>
              <Text style={styles.financingTitle}>Seguro</Text>
              <Text style={styles.financingSubtitle}>
                {receipt.product.insurance ? receipt.product.insurance : 'Sem seguro'}
              </Text>
            </View>
            <View style={styles.rowFinancingData}>
              <Text style={styles.financingTitle}>Valor do seguro</Text>
              <Text style={styles.financingSubtitle}>{receipt.product.insuranceValue !== '-' ? receipt.product.insuranceValue : 'R$ 0,00'}</Text>
            </View>
            <View style={styles.rowFinancingData}>
              <Text style={styles.financingTitle}>Valor dos acessórios</Text>
              <Text style={styles.financingSubtitle}>{receipt.product.accessoryValue !== '-' ? receipt.product.accessoryValue : 'R$ 0,00'}</Text>
            </View>
            <View style={styles.rowFinancingData}>
              <Text style={styles.financingTitle}>Subtotal dos produtos</Text>
              <Text style={styles.financingSubtitle}>{receipt.product.productsSubtotal}</Text>
            </View>
            <View style={styles.dashContainer}>
              {[...Array(35)].map((_, index) => (
                <View key={index} style={styles.dash} />
              ))}
            </View>
            <View style={styles.rowFinancingData}>
              <Text style={styles.financingTitle}>Valor de entrada</Text>
              <Text style={styles.financingSubtitle}>{receipt.product.inputValue}</Text>
            </View>
            <View style={styles.rowFinancingData}>
              <Text style={styles.financingTitle}>Parcelas</Text>
              <Text style={styles.financingSubtitle}>{receipt.product.numberOfInstallments}x de {receipt.product.installmentValue}</Text>
            </View>
            <View style={styles.rowFinancingData}>
              <Text style={styles.financingTitle}>Vencimento da 1ª parcela</Text>
              <Text style={styles.financingSubtitle}>{receipt.product.firstInstallmentDate}</Text>
            </View>
            <View style={styles.rowFinancingData}>
              <Text style={styles.financingTitle}>Total a pagar (c/juros)</Text>
              <Text style={styles.financingSubtitle}>{receipt.product.totalValue}</Text>
            </View>
            <View style={styles.rowFinancingData}>
              <Text style={styles.financingTitle}>Valor financiado/Valor repasse</Text>
              <Text style={styles.financingLabel}>{receipt.product.financingValue}</Text>
            </View>
          </View>
        </View>
        <View style={styles.footer}>
          <Text>Atenciosamente, equipe Dimo.</Text>
          <View>
            <Text>{receipt.nowDate}.</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export const Receipt = () => {
  const { proposal, person, contracting, product, device, seller } = getLocalStorageData(RECEIPT) || {};

  const receipt = receiptFactory(proposal, person, contracting, product, device, seller);

  return (
    <div style={{ height: '100vh', width: '100%' }}>
      <PDFViewer width="100%" height="100%">
        {ReceiptConstructor(receipt)}
      </PDFViewer>
    </div>
  );
};
